import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import SiteHeader from "../components/SiteHeader.jsx";
import SiteFooter from "../components/SiteFooter.jsx";
import SearchInput from "../components/SearchInput.jsx";
import {OpenProtGlobalContext} from "../../main.jsx";

const AcknowledgementsUrl = new URL("./Acknowledgements.md", import.meta.url)


const Home = ({})=> {

    const navigate = useNavigate()

    const {setGlobalState} = useContext(OpenProtGlobalContext)
    const [acknowledgementsMarkdown, setAcknowledgementsMarkdown] = useState(null)

    const searchClick = (species_sn, query) => {

        const go = () => {
            setGlobalState({lastQuery: query})
            navigate(`/proteins/${species_sn}`)
        }

        setTimeout(go, 1)
    }

    const openCard = (card) => {
        if (card.target === "_blank") {
            window.open(card.href, "_blank", "noopener,noreferrer")
            return
        }

        if (card.href.startsWith("/")) {
            navigate(card.href)
            return
        }

        window.location.href = card.href
    }

    useEffect(() => {
        const fetchAcknowledgements = async () => {
            try {
                const response = await fetch(AcknowledgementsUrl)
                if (!response.ok) {
                    throw new Error(`Unable to load acknowledgements markdown: ${response.status}`)
                }

                const markdown = await response.text()
                setAcknowledgementsMarkdown(markdown)
            } catch {
                setAcknowledgementsMarkdown("Acknowledgements content could not be loaded.")
            }
        }

        fetchAcknowledgements()
    }, [])

    const landingCards = [
        {
            title: "Download",
            subtitle: "Protein Libraries",
            description: "Protein libraries in FASTA format, plus BED files and tabular protein characteristics for downstream analyses.",
            href: "/downloads",
            target: "_self",
            clickable: true,
            image: "/img/landing_downloads.svg",
        },
        {
            title: "RiboSeq Evidence",
            subtitle: "RiboCrypt",
            description: "Explore frame-resolved ribosome profiling evidence and quality metrics integrated for OpenProt ORFs.",
            href: "https://ribocrypt.org/",
            target: "_blank",
            clickable: false,
            image: "/img/ribocrypt.svg",
            references: [
                {
                    href: "https://ribocrypt.org/",
                    label: "RiboCrypt website",
                },
            ],
        },
        {
            title: "Evolutionary Signal",
            subtitle: "ORBL",
            description: "Inspect ORF Relative Branch Length metrics to evaluate cross-species conservation and evolutionary constraint.",
            href: "/documentation",
            target: "_self",
            clickable: false,
            image: "/img/landing_orbl.svg",
            references: [
                {
                    href: "https://pubmed.ncbi.nlm.nih.gov/42092140/",
                    label: "ORBL, PMID: 42092140",
                },
            ],
        },
        {
            title: "Structure Insight",
            subtitle: "3D Predictions",
            description: "Explore structure predictions for alternative proteins using model-driven workflows and confidence summaries.",
            href: "/documentation",
            target: "_self",
            image: "/img/Frontpage_structure.png",
            references: [
                {
                    href: "https://pubmed.ncbi.nlm.nih.gov/38718835/",
                    label: "AlphaFold 3, PMID: 38718835",
                },
                {
                    href: "https://doi.org/10.1101/2022.07.21.500999",
                    label: "OmegaFold, DOI: 10.1101/2022.07.21.500999",
                },
            ],
        },
        {
            title: "Subcellular Localization",
            subtitle: "DeepLoc 2.1",
            description: "Browse DeepLoc 2.1 predictions to explore likely cellular compartments and membrane localization patterns.",
            href: "/documentation",
            target: "_self",
            image: "/img/localization.png",
            references: [
                {
                    href: "https://pubmed.ncbi.nlm.nih.gov/38587188/",
                    label: "DeepLoc 2.1, PMID: 38587188",
                },
            ],
        },
        {
            title: "Motif Discovery",
            subtitle: "Short Linear Motifs",
            description: "Explore predicted SLiMs in intrinsically disordered regions based on the ELM motif resource.",
            href: "/documentation",
            target: "_self",
            image: "/img/slim.png",
            references: [
                {
                    href: "https://pubmed.ncbi.nlm.nih.gov/37962385/",
                    label: "ELM v1.4, PMID: 37962385",
                },
            ],
        },
        {
            title: "PTM Discovery",
            subtitle: "DMO Search (MSFragger)",
            description: "Inspect post-translational modifications detected by delta-mass-offset searches enabled by MSFragger.",
            href: "/documentation",
            target: "_self",
            image: "/img/landing_dmo.svg",
            references: [
                {
                    href: "https://pubmed.ncbi.nlm.nih.gov/28394336/",
                    label: "MSFragger, PMID: 28394336",
                },
            ],
        },
    ]


    return <>
        <SiteHeader/>

        <section className="landing-hero">
            <div className="landing-hero-glow" aria-hidden="true"/>
            <div className="landing-hero-inner">
                <h1 className="landing-hero-title">Explore the extended proteome</h1>
                <p className="landing-hero-subtitle">Search proteins, inspect multi-omics evidence, and navigate non-canonical proteome annotations in one place.</p>
                <div className="landing-search-shell">
                    <SearchInput species_sn={"HS"} onSearchClick={searchClick}/>
                </div>
                <div className="mt-3 text-center">
                    <a
                        href="/data-sources"
                        className="text-sm text-slate-500 underline underline-offset-2 hover:font-semibold"
                    >
                        Browse the re-analysed data sources
                    </a>
                </div>
            </div>
        </section>

        <div className="landing-page-content">
            <section className="landing-surface landing-surface-cards">
                <div className="landing-card-rail">
                    <div className="landing-card-track">
                        {
                            [...landingCards, ...landingCards].map((card, idx) =>
                                (() => {
                                    const isClickable = card.clickable === true

                                    return (
                                <div
                                    key={`${card.title}-${idx}`}
                                    className={`landing-card${isClickable ? "" : " landing-card-static"}`}
                                    role={isClickable ? "link" : undefined}
                                    tabIndex={isClickable ? 0 : undefined}
                                    onClick={isClickable ? () => openCard(card) : undefined}
                                    onKeyDown={isClickable ? (event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault()
                                            openCard(card)
                                        }
                                    } : undefined}
                                >
                                    <img className="landing-card-image" src={card.image} alt="" aria-hidden="true"/>
                                    <div className="landing-card-overlay">
                                        {
                                            card.references &&
                                            <div className="landing-card-references">
                                                {
                                                    card.references.map((reference, refIdx) =>
                                                        <a
                                                            key={`${reference.label}-${refIdx}`}
                                                            href={reference.href}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="landing-card-reference-link"
                                                            onClick={(event) => event.stopPropagation()}
                                                        >
                                                            {reference.label}
                                                        </a>
                                                    )
                                                }
                                            </div>
                                        }
                                        <h2 className="landing-card-title">{card.title}</h2>
                                        <h3 className="landing-card-subtitle">{card.subtitle}</h3>
                                        <p className="landing-card-description">{card.description}</p>
                                    </div>
                                </div>
                                    )
                                })()
                            )
                        }
                    </div>
                </div>
            </section>

            <section className="landing-surface">
                <h2 className="text-2xl m-2 font-semibold">The concept behind OpenProt</h2>
                <div className="grid gap-8 md:grid-cols-2">
                    <div className="m-2">
                    <h3 className="text-lg my-2 font-semibold">Current annotations</h3>
                    Current genome annotations hold limiting criteria for Open Reading Frames (ORF) including a minimal ORF length of 100 codons and a single ORF per transcript. Transcripts that do not meet these criteria are labeled non-coding (ncRNAs) and transcripts from unprocessed pseudogenes are also systematically annotated non-coding.
                    </div>
                    <div className="m-auto">
                    <img src="/img/current_model.png" width="500"/>
                    </div>
                    <div className="m-2 mt-8">
                    <h3 className="text-lg my-2 font-semibold">OpenProt annotations</h3>
                    OpenProt relaxes traditional annotation criteria by including all ORFs longer than 30 codons and allowing multiple ORFs per transcript as well as those encoded in ncRNAs and transcripts of pseudogenes. OpenProt offers a deeper description and thus a more realistic and biologically relevant perspective of the proteome.                    </div>
                    <div className="mx-auto mt-8">
                    <img src="/img/OpenProt_model.png" width="500"/>
                    </div>
                </div>
            </section>

            <section className="landing-surface">
                <h2 className="text-2xl m-2 font-semibold">OpenProt discoveries: re-interpret already acquired data</h2>
                <div className="m-2">
                    The annotation of sequences is central to current research in biomolecular sciences. The addition of unannotated protein sequences in the OpenProt protein library has resulted in many important discoveries in the human proteome through the re-analysis of publicly available data. Many of these have been selected for further investigation:
                </div>
                <div className="grid gap-6 mx-2 md:mx-8 md:grid-cols-2">
                    <ul className="list-disc m-5">
                        <li>
                            <p>The FUS gene is dual-coding with both proteins contributing to FUS-mediated toxicity. <a className="text-blue-800" href="https://doi.org/10.15252/embr.202050640" target="_blank">https://doi.org/10.15252/embr.202050640</a></p>
                        </li>
                        <li>
                            <p>The Protein Coded by a Short Open Reading Frame, Not by the Annotated Coding Sequence, Is the Main Gene Product of the Dual-Coding Gene MIEF1. <a className="text-blue-800" href="https://doi.org/10.1074/mcp.RA118.000593" target="_blank">https://doi.org/10.1074/mcp.RA118.000593</a></p>
                        </li>
                        <li>
                            <p>Potentiation of B2 receptor signaling by AltB2R, a newly identified alternative protein encoded in the human bradykinin B2 receptor gene. <a className="text-blue-800" href="https://doi.org/10.1016/j.jbc.2021.100329" target="_blank">https://doi.org/10.1016/j.jbc.2021.100329</a></p>
                        </li>
                    </ul>
                    <ul className="list-disc m-5">
                        <li>
                            <p>UBB pseudogene 4 encodes functional ubiquitin variants. <a className="text-blue-800" href="https://doi.org/10.1038/s41467-020-15090-6" target="_blank">https://doi.org/10.1038/s41467-020-15090-6</a></p>
                        </li>
                        <li>
                            <p>An overlapping reading frame in the PRNP gene encodes a novel polypeptide distinct from the prion protein. <a className="text-blue-800" href="https://doi.org/10.1096/fj.10-173815" target="_blank">https://doi.org/10.1096/fj.10-173815</a></p>
                        </li>
                        <li>
                            <p>An out-of-frame overlapping reading frame in the ataxin-1 coding sequence encodes a novel ataxin-1 interacting protein. <a className="text-blue-800" href="https://doi.org/10.1074/jbc.M113.472654" target="_blank">https://doi.org/10.1074/jbc.M113.472654</a></p>
                        </li>
                    </ul>
                </div>
            </section>

            <section className="landing-surface">
                <h2 className="text-2xl m-2 font-semibold">The OpenProt pipeline</h2>
                <div className="grid gap-8 md:grid-cols-2">
                    <div className="m-auto">
                    <img src="/img/OpenProt_pipeline.png" width="350"/>
                    </div>                    
                    <div className="m-2">
                    <h3 className="text-lg my-2 font-semibold">Prediction pipeline</h3>
                    The OpenProt ORF prediction pipeline starts from an exhaustive description of the transcriptome consisting of all RNA transcripts reported by both Ensembl and NCBI RefSeq. A 3-frame in silico translation then yields the ORFeome: any ORF longer than 30 codons in any frame of any transcript. This ORFeome is then filtered to categorize predicted ORFs. The first filter retrieves all known proteins, or reference proteins (all ORF already annotated in Ensembl, NCBI RefSeq, and/or UniProtKB). The second filter is based on the homology of currently not annotated ORFs with the refProt of the same gene (if applicable), and retrieves novel predicted isoforms. The remaining ORFs encode novel proteins, called alternative proteins (altProts).</div>
                    <div className="mx-auto mt-8">
                    <img src="/img/OpenProt_evidence_pipeline.png" width="350"/>
                    </div>
                    <div className="m-2 mt-8">
                    <h3 className="text-lg my-2 font-semibold">Evidence pipeline</h3>
                    <ul className="list-disc m-5">
                        <li>
                            <span className="font-semibold">Conservation evidence</span>: for every ORF annotated, OpenProt identifies orthologs and paralogs (across the 10 species currently supported by OpenProt).
                        </li>
                        <li>
                            <span className="font-semibold">Translation evidence</span>: Publicly available ribosome profiling datasets are re-analysed using the Price algorithm. This gathers translation evidence for any ORF annotated in OpenProt.
                        </li>
                        <li>
                            <span className="font-semibold">Expression evidence</span>: Publicly available mass spectrometry datasets are re-analysed using multiple search engines. This gathers expression evidence for any ORF annotated in OpenProt.
                        </li>
                    </ul>
                    </div>
                </div>
                
            </section>

            <section className="landing-surface landing-surface-compact">
                <h2 className="text-l m-2 font-semibold">Older versions of OpenProt</h2>
                <div className="m-2">
                <a className="cursor-pointer hover:underline text-blue-600" href='https://archive.openprot.org/'> Openprot 1.6</a>
                </div>
                
                
            </section>

            <section className="landing-surface">
                <h2 className="text-2xl m-2 font-semibold">Acknowledgements</h2>
                <div className="m-2 prose max-w-none prose-a:text-blue-700 landing-ack-prose">
                    {
                        acknowledgementsMarkdown
                            ? <ReactMarkdown>{acknowledgementsMarkdown}</ReactMarkdown>
                            : <p>Loading acknowledgements...</p>
                    }
                </div>
            </section>


        </div>
    <SiteFooter/>
  </>
}

export default Home
